import React from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import CloseIcon from "@mui/icons-material/Close";
import { FlowTemplateAction } from "../../../../../stores/flowsStore";
import { useTemplateConfiguration } from "./useTemplateConfiguration";

interface TemplateConfigurationProps {
  templateAction: FlowTemplateAction | null;
  onChange: (templateAction: FlowTemplateAction | null) => void;
  onRemove?: () => void;
}

export const TemplateConfiguration: React.FC<TemplateConfigurationProps> = ({
  templateAction,
  onChange,
  onRemove,
}) => {
  const {
    templates,
    templatesLoading,
    generatedDocumentsForFlows,
    sourceType,
    handleSourceTypeChange,
    handleTemplateChange,
    handleDocumentChange,
  } = useTemplateConfiguration({ templateAction, onChange });

  return (
    <Paper variant="outlined" sx={{ p: 2, position: "relative" }}>
      {onRemove && (
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <DescriptionIcon color="action" />
        <Typography variant="subtitle1">Attach Document</Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={sourceType}
          exclusive
          onChange={handleSourceTypeChange}
          size="small"
          fullWidth
        >
          <ToggleButton value="template" sx={{ flex: 1 }}>
            <DescriptionIcon sx={{ mr: 1, fontSize: 18 }} />
            Use Template
          </ToggleButton>
          <ToggleButton value="generated_document" sx={{ flex: 1 }}>
            <FolderIcon sx={{ mr: 1, fontSize: 18 }} />
            Use Saved Document
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {sourceType === "template" ? (
        <TextField
          select
          fullWidth
          label="Select Template"
          value={templateAction?.template_id || ""}
          onChange={(e) => handleTemplateChange(e.target.value)}
          disabled={templatesLoading}
          helperText="Select a document template to generate when this flow is triggered"
        >
          <MenuItem value="">
            <em>Select a template...</em>
          </MenuItem>
          {templates.map((template) => (
            <MenuItem key={template.identifier} value={template.identifier}>
              {template.name}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField
          select
          fullWidth
          label="Select Saved Document"
          value={templateAction?.document_id || ""}
          onChange={(e) => handleDocumentChange(e.target.value)}
          helperText="Select a previously generated document to attach when this flow is triggered"
        >
          <MenuItem value="">
            <em>Select a saved document...</em>
          </MenuItem>
          {generatedDocumentsForFlows.map((doc) => (
            <MenuItem key={doc.id} value={doc.id}>
              {doc.name}
              {doc.client_name && (
                <Typography component="span" sx={{ ml: 1, color: "text.secondary", fontSize: 12 }}>
                  ({doc.client_name})
                </Typography>
              )}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Paper>
  );
};
